import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, getMe } from '../api/auth';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) { setLoading(false); return; }

    // Stale-while-revalidate : affiche immédiatement le user en cache
    // (localStorage) si présent, puis rafraîchit en arrière-plan via getMe().
    // Évite l'écran blanc d'attente quand Railway est lent à répondre.
    let hasCache = false;
    try {
      const cached = JSON.parse(localStorage.getItem('user') || 'null');
      if (cached) {
        setUser(cached);
        setLoading(false);
        hasCache = true;
      }
    } catch { /* ignore */ }

    getMe()
      .then(res => {
        setUser(res.data.user);
        localStorage.setItem('user', JSON.stringify(res.data.user));
      })
      .catch((err) => {
        // Serveur injoignable (redeploiement, reseau) : on garde la session.
        if (err.response?.status !== 401) return;
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        setUser(null);
      })
      .finally(() => { if (!hasCache) setLoading(false); });
  }, []);

  const login = async (email, password) => {
    const res = await apiLogin(email, password);
    localStorage.setItem('token', res.data.token);
    localStorage.setItem('user', JSON.stringify(res.data.user));
    setUser(res.data.user);
    if (res.data.mustChangePassword) {
      setMustChangePassword(true);
    }
    return res.data;
  };

  const clearMustChangePassword = () => setMustChangePassword(false);

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setUser(null);
  };

  const userRoles = user?.roles && user.roles.length > 0 ? user.roles : (user?.role ? [user.role] : []);

  const hasRole = (...roles) => user && userRoles.some(r => roles.includes(r));

  const ALL_COMPANIES = ['CocoGames', 'WinWin Tahiti', 'LadageMaxime Academy'];

  const hasCompany = (company) => {
    if (!user) return false;
    if (userRoles.includes('admin')) return true;
    return user.companies && user.companies.includes(company);
  };

  const userCompanies = userRoles.includes('admin')
    ? ALL_COMPANIES
    : (user?.companies || []);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, hasRole, hasCompany, userCompanies, mustChangePassword, clearMustChangePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
